import React, { useEffect, useState } from "react";
import { collection, addDoc, getDocs, query, where, orderBy, serverTimestamp, doc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import { Star, ThumbsUp } from "lucide-react";
import { formatDate } from "../../utils/format";
import toast from "react-hot-toast";

function StarPicker({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div style={{ display: "flex", gap: 4 }}>
      {[1,2,3,4,5].map(s => (
        <button key={s} type="button"
          onMouseEnter={() => setHover(s)} onMouseLeave={() => setHover(0)}
          onClick={() => onChange(s)}
          style={{ border: "none", background: "transparent", cursor: "pointer", padding: 2 }}>
          <Star size={24} fill={(hover || value) >= s ? "#F59E0B" : "none"} color="#F59E0B" />
        </button>
      ))}
    </div>
  );
}

function RatingBar({ count, total, stars }) {
  const pct = total ? Math.round((count / total) * 100) : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
      <span style={{ fontSize: 12, width: 12, textAlign: "right" }}>{stars}</span>
      <Star size={11} fill="#F59E0B" color="#F59E0B" />
      <div style={{ flex: 1, height: 6, background: "var(--border)", borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: pct + "%", height: "100%", background: "#F59E0B", borderRadius: 3, transition: "width 0.6s" }} />
      </div>
      <span style={{ fontSize: 12, color: "var(--text-muted)", width: 28 }}>{count}</span>
    </div>
  );
}

export default function ReviewSection({ productId }) {
  const { user, profile } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ rating: 0, title: "", body: "" });
  const [hasReviewed, setHasReviewed] = useState(false);

  async function load() {
    const snap = await getDocs(query(
      collection(db, "reviews"),
      where("productId", "==", productId),
      orderBy("createdAt", "desc")
    ));
    const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setReviews(data);
    if (user) setHasReviewed(data.some(r => r.userId === user.uid));
    setLoading(false);
  }

  useEffect(() => { load(); }, [productId, user]);

  async function submitReview(e) {
    e.preventDefault();
    if (!form.rating) return toast.error("Please select a star rating");
    setSubmitting(true);
    try {
      await addDoc(collection(db, "reviews"), {
        productId,
        userId: user.uid,
        userName: profile?.fullName || "Anonymous",
        rating: form.rating,
        title: form.title,
        body: form.body,
        helpful: 0,
        verified: true,
        createdAt: serverTimestamp(),
      });
      // Update product average rating
      const all = [...reviews, { rating: form.rating }];
      const avg = all.reduce((s, r) => s + r.rating, 0) / all.length;
      await updateDoc(doc(db, "products", productId), {
        rating: Math.round(avg * 10) / 10,
        reviewCount: all.length,
      });
      toast.success("Review submitted!");
      setForm({ rating: 0, title: "", body: "" });
      setShowForm(false);
      load();
    } catch (err) {
      toast.error("Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  }

  async function markHelpful(reviewId, current) {
    if (!user) return toast.error("Sign in to mark reviews as helpful");
    await updateDoc(doc(db, "reviews", reviewId), { helpful: current + 1 });
    load();
  }

  // Stats
  const total = reviews.length;
  const avg = total ? reviews.reduce((s, r) => s + r.rating, 0) / total : 0;
  const dist = [5,4,3,2,1].map(s => ({ stars: s, count: reviews.filter(r => r.rating === s).length }));

  return (
    <div style={{ marginTop: 56 }}>
      <h2 style={{ fontFamily: "var(--font-head)", fontSize: 22, fontWeight: 700, marginBottom: 28 }}>
        Customer Reviews {total > 0 && <span style={{ fontSize: 15, fontWeight: 400, color: "var(--text-muted)" }}>({total})</span>}
      </h2>

      {/* Rating summary */}
      {total > 0 && (
        <div style={{ display: "flex", gap: 40, alignItems: "flex-start", padding: 24, background: "var(--surface)", borderRadius: 16, marginBottom: 28, flexWrap: "wrap" }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontFamily: "var(--font-head)", fontSize: 56, fontWeight: 800, lineHeight: 1, color: "var(--text)" }}>{avg.toFixed(1)}</div>
            <div style={{ display: "flex", justifyContent: "center", gap: 2, margin: "8px 0 4px" }}>
              {[1,2,3,4,5].map(s => <Star key={s} size={16} fill={s <= Math.round(avg) ? "#F59E0B" : "none"} color="#F59E0B" />)}
            </div>
            <div style={{ fontSize: 13, color: "var(--text-muted)" }}>{total} review{total !== 1 ? "s" : ""}</div>
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            {dist.map(({ stars, count }) => (
              <RatingBar key={stars} stars={stars} count={count} total={total} />
            ))}
          </div>
        </div>
      )}

      {/* Write review button */}
      {user && !hasReviewed && !showForm && (
        <button onClick={() => setShowForm(true)} className="btn btn-secondary" style={{ marginBottom: 28 }}>
          Write a Review
        </button>
      )}

      {/* Review form */}
      {showForm && (
        <div className="card" style={{ padding: 24, marginBottom: 28 }}>
          <h3 style={{ fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 16, marginBottom: 20 }}>Your Review</h3>
          <form onSubmit={submitReview} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div className="form-group">
              <label>Star Rating *</label>
              <StarPicker value={form.rating} onChange={r => setForm(f => ({ ...f, rating: r }))} />
            </div>
            <div className="form-group">
              <label>Review Title</label>
              <input className="form-input" placeholder="Summarize your experience" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Review *</label>
              <textarea className="form-input" rows={4} required placeholder="Share your experience with this product…" value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} style={{ resize: "vertical" }} />
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button type="button" onClick={() => setShowForm(false)} className="btn btn-secondary">Cancel</button>
              <button type="submit" disabled={submitting} className="btn btn-primary">{submitting ? "Submitting…" : "Submit Review"}</button>
            </div>
          </form>
        </div>
      )}

      {/* Reviews list */}
      {loading ? (
        <div className="loader"><div className="spinner" /></div>
      ) : reviews.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--text-muted)" }}>
          <Star size={36} style={{ marginBottom: 12, opacity: 0.3 }} />
          <p>No reviews yet. Be the first to review this product!</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {reviews.map(r => (
            <div key={r.id} style={{ paddingBottom: 20, borderBottom: "1px solid var(--border)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: "var(--brand-light)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13, color: "var(--brand)" }}>
                      {r.userName?.[0]?.toUpperCase()}
                    </div>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{r.userName}</span>
                    {r.verified && <span className="badge badge-success" style={{ fontSize: 11 }}>✓ Verified Purchase</span>}
                  </div>
                  <div style={{ display: "flex", gap: 2 }}>
                    {[1,2,3,4,5].map(s => <Star key={s} size={13} fill={s <= r.rating ? "#F59E0B" : "none"} color="#F59E0B" />)}
                  </div>
                </div>
                <span style={{ fontSize: 13, color: "var(--text-muted)" }}>{formatDate(r.createdAt)}</span>
              </div>
              {r.title && <p style={{ fontWeight: 600, marginBottom: 4, fontSize: 15 }}>{r.title}</p>}
              <p style={{ fontSize: 14, lineHeight: 1.7, color: "var(--text-muted)", marginBottom: 10 }}>{r.body}</p>
              <button onClick={() => markHelpful(r.id, r.helpful || 0)}
                style={{ display: "flex", alignItems: "center", gap: 6, border: "1px solid var(--border)", background: "transparent", padding: "4px 12px", borderRadius: 100, fontSize: 12, cursor: "pointer", color: "var(--text-muted)" }}>
                <ThumbsUp size={12} /> Helpful ({r.helpful || 0})
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
